import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Home, Heart, Briefcase, Shield, Users, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { submitLead } from "@/lib/leads";
import { trackQuoteStart, trackQuoteStep, trackQuoteSubmit } from "@/lib/tracking";
import PhoneLink from "@/components/PhoneLink";
import HumanCheckField from "@/components/HumanCheckField";
import { useHumanCheck } from "@/hooks/use-human-check";
import ConsentNotice from "@/components/ConsentNotice";

const insuranceTypes = [
  { id: "auto", label: "Auto", icon: Car },
  { id: "home", label: "Home", icon: Home },
  { id: "life", label: "Life", icon: Heart },
  { id: "business", label: "Business", icon: Briefcase },
  { id: "health", label: "Health", icon: Shield },
  { id: "group", label: "Group Benefits", icon: Users },
];

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 40 : -40,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction < 0 ? 40 : -40,
    opacity: 0,
  }),
};

const QuoteFunnel = () => {
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get("service") || "";
  const initialCity = searchParams.get("city") || "";
  const validInitialService = insuranceTypes.some((t) => t.id === initialService) ? initialService : "";

  const [step, setStep] = useState(validInitialService ? 1 : 0);
  const [direction, setDirection] = useState(1);
  const [selectedType, setSelectedType] = useState(validInitialService);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", zip: "", _honey: "" });
  const [submitted, setSubmitted] = useState(false);
  const humanCheck = useHumanCheck();

  // Track an auto-started funnel from a city deep link.
  useEffect(() => {
    if (validInitialService) trackQuoteStart(validInitialService);
  }, [validInitialService]);

  const goNext = () => {
    setDirection(1);
    setStep((s) => {
      const next = s + 1;
      trackQuoteStep(next);
      return next;
    });
  };
  const goBack = () => {
    setDirection(-1);
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (formData._honey) return;
    if (!humanCheck.validate()) return;
    trackQuoteSubmit(selectedType);
    const res = await submitLead({
      form_type: "quote",
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      zip: formData.zip,
      insurance_type: selectedType,
      metadata: initialCity ? { city: initialCity } : undefined,
    });
    if (res.success) setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-surface rounded-funnel p-8 md:p-10 shadow-funnel max-w-[600px] mx-auto text-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
          <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
          <h3 className="text-2xl font-semibold mb-2">Thanks, {formData.name.split(" ")[0] || "there"}!</h3>
          <p className="text-muted-foreground leading-relaxed mb-5">
            A Phoenix-based broker is reviewing your <span className="tabular-nums">32</span> carrier options now.
            We'll reach out within <span className="font-semibold text-foreground">1 business hour</span>.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted">
            <span className="text-sm text-muted-foreground">Need it sooner?</span>
            <PhoneLink location="quote_confirmation" className="text-sm font-semibold text-primary hover:text-primary/80" />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-funnel p-6 md:p-10 shadow-funnel max-w-[600px] mx-auto">
      {/* Progress */}
      <div className="flex gap-1.5 mb-8">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
              i <= step ? "bg-copper" : "bg-muted"
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        {step === 0 && (
          <motion.div
            key="step0"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <h2 className="text-xl font-semibold mb-1">What are we protecting?</h2>
            <p className="text-muted-foreground text-sm mb-6">Select the type of coverage you need.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {insuranceTypes.map((t) => {
                const Icon = t.icon;
                const isSelected = selectedType === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setSelectedType(t.id);
                      trackQuoteStart(t.id);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 min-h-[88px] ${
                      isSelected
                        ? "border-copper bg-copper/5 shadow-sm"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    <Icon className={`w-6 h-6 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                    <span className={`text-sm font-medium ${isSelected ? "text-foreground" : "text-muted-foreground"}`}>
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <Button
              onClick={goNext}
              disabled={!selectedType}
              className="w-full mt-6 h-12 text-base"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div
            key="step1"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <h3 className="text-xl font-semibold mb-1">Your contact info</h3>
            <p className="text-muted-foreground text-sm mb-6">So we can send your personalized quotes.</p>
            <div className="space-y-4">
              {/* Honeypot */}
              <input type="text" name="_honey" value={formData._honey} onChange={(e) => setFormData({ ...formData, _honey: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Full Name</label>
                <input
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-input bg-surface text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all duration-200"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Email</label>
                <input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-input bg-surface text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all duration-200"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Phone</label>
                <input
                  type="tel"
                  placeholder="(602) 555-1234"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-input bg-surface text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all duration-200"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={goBack} className="h-12">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={goNext}
                disabled={!formData.name || !formData.email || !formData.phone}
                className="flex-1 h-12 text-base"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div
            key="step2"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            <h3 className="text-xl font-semibold mb-1">Almost there!</h3>
            <p className="text-muted-foreground text-sm mb-6">Just a couple more details to get your best rates.</p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">ZIP Code</label>
                <input
                  type="text"
                  placeholder="85001"
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-input bg-surface text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all duration-200"
                  maxLength={5}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Current Coverage</label>
                <select className="w-full h-12 px-4 rounded-xl border border-input bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-copper/20 focus:border-copper transition-all duration-200">
                  <option value="">Select one</option>
                  <option value="yes">Yes, I'm currently insured</option>
                  <option value="no">No current coverage</option>
                  <option value="switching">Looking to switch providers</option>
                </select>
              </div>
              <HumanCheckField
                question={humanCheck.challenge.question}
                value={humanCheck.userAnswer}
                onChange={humanCheck.setUserAnswer}
                error={humanCheck.error}
                verified={humanCheck.verified}
              />
            </div>
            <ConsentNotice />
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={goBack} className="h-12">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!formData.zip || !humanCheck.verified}
                className="flex-1 h-12 text-base"
              >
                Get My Free Quote
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuoteFunnel;
